import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trackError } from '@proposify/frontend-observability';
import { QueryKeys } from 'utils/queryKeys';
import { linkQueries } from './linkQueries';
import type { ContentLink, FlexDataLink, RelatedLink } from './types';

export const useLinkCrmObject = (contentId: string, onRelatedLinksResolved?: (relatedLinks: RelatedLink[]) => void) => {
  const queryClient = useQueryClient();
  const queryKey = QueryKeys.getContentFlexDataLinks(contentId);
  const crmVariablesQueryKey = [...QueryKeys.crmVariables, contentId];

  const { mutate: linkObject } = useMutation({
    ...linkQueries.link(contentId),
    onMutate: async (obj: FlexDataLink) => {
      await queryClient.cancelQueries({ queryKey });
      const optimisticEntry: ContentLink = {
        fromObject: { dataSourceType: 'Proposify', tenantId: '', objectType: 'Document', objectId: contentId, objectLabel: '' },
        toObject: {
          dataSourceType: obj.dataSourceType,
          tenantId: obj.tenantId,
          objectType: obj.objectType,
          objectId: obj.objectId,
          objectLabel: obj.objectLabel,
        },
        normalizedType: obj.normalizedType,
        qualifier: obj.qualifier || null,
        status: 'active',
        createdAt: new Date().toISOString(),
      };
      queryClient.setQueryData<ContentLink[]>(queryKey, (prev) => [...(prev ?? []), optimisticEntry]);
    },
    onSuccess: (data, obj) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: crmVariablesQueryKey });
      const { relatedLinks } = data;
      if (obj.normalizedType === 'Opportunity' && relatedLinks?.length) {
        onRelatedLinksResolved?.(relatedLinks);
      }
    },
    onError: (error: Error) => {
      trackError(error);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return { linkObject };
};
